import { defineStore } from 'pinia';
import { formatDate } from '@/utils/date';
import { productivityApi } from '@/services/productivityService';

function getDefaultDates() {
  const today = new Date();
  const priorDate = new Date();
  priorDate.setMonth(today.getMonth() - 12);

  return {
    startDate: formatDate(priorDate, 'yyyy-MM-dd'),
    endDate: formatDate(today, 'yyyy-MM-dd'),
  };
}

const { startDate: defaultStartDate, endDate: defaultEndDate } =
  getDefaultDates();

export const useProductivityStore = defineStore('productivity', {
  state: () => ({
    overviewData: [],
    overviewLoading: false,

    detailsData: [],
    detailsLoading: false,

    filters: {
      userStatus: 'all',
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      selectedUsers: [],
    },

    statusFilter: [],

    detailsPagination: {
      page: 1,
      limit: 15,
      sortKey: 'activity_date',
      sortOrder: 'desc',
      totalRows: 0,
    },

    overviewBarStats: {
      inTrouble: 0,
      atRisk: 0,
      atGoal: 0,
    },

    overviewBarUserCounts: {
      inTrouble: 0,
      atRisk: 0,
      atGoal: 0,
    },

    editingEntryId: null,
    journalEdits: [],

    _overviewFetchCount: 0,
    _detailsFetchCount: 0,
  }),

  getters: {
    chartData: state => {
      if (!state.overviewData?.length) {
        return { categories: [], values: [] };
      }

      const sorted = [...state.overviewData].sort((a, b) => {
        const dateA = a.activity_month || a.activity_date;
        const dateB = b.activity_month || b.activity_date;
        return new Date(dateA) - new Date(dateB);
      });

      return {
        categories: sorted.map(item => {
          const dateStr = item.activity_month || item.activity_date;
          return formatMonth(dateStr);
        }),
        values: sorted.map(item => Math.round(item.completion_rate * 100)),
      };
    },

    averageProductivity: state => {
      if (!state.overviewData?.length) return 0;
      const sum = state.overviewData.reduce(
        (acc, item) => acc + item.completion_rate * 100,
        0
      );
      return Math.round(sum / state.overviewData.length);
    },

    getExportFilters: state => {
      const sel = state.filters.selectedUsers;
      const userIds = sel === null ? [] : sel?.length ? sel.map(u => u.id) : [];
      const us = state.filters.userStatus;
      return {
        start_date: state.filters.startDate,
        end_date: state.filters.endDate,
        user_ids: userIds,
        active_only: us === 'active' ? true : us === 'inactive' ? false : null,
        status:
          state.statusFilter.length > 0 ? state.statusFilter.join(',') : null,
      };
    },
  },

  actions: {
    async fetchOverview() {
      this.overviewLoading = true;
      try {
        if (this.filters.selectedUsers === null) {
          this.overviewData = [];
          this.overviewBarStats = { inTrouble: 0, atRisk: 0, atGoal: 0 };
          this.overviewBarUserCounts = { inTrouble: 0, atRisk: 0, atGoal: 0 };
          this.overviewLoading = false;
          return;
        }

        this._overviewFetchCount += 1;
        const data = await productivityApi.getOverview(
          this._buildFilterParams()
        );
        this.overviewData = data || [];
        const isEmptyWithAllSelected =
          this.overviewData.length === 0 &&
          this.filters.selectedUsers !== null &&
          this.filters.selectedUsers.length === 0 &&
          this._overviewFetchCount > 1;
        if (isEmptyWithAllSelected) {
          this.filters.selectedUsers = null;
          this.overviewBarStats = { inTrouble: 0, atRisk: 0, atGoal: 0 };
          this.overviewBarUserCounts = { inTrouble: 0, atRisk: 0, atGoal: 0 };
        }
      } catch {
        this.overviewData = [];
        this.overviewBarStats = { inTrouble: 0, atRisk: 0, atGoal: 0 };
        this.overviewBarUserCounts = { inTrouble: 0, atRisk: 0, atGoal: 0 };
      } finally {
        this.overviewLoading = false;
      }
    },

    async fetchDetails(page = 1, sortKey = null, sortOrder = null) {
      this.detailsLoading = true;
      try {
        // If no users selected (null), clear data and return
        if (this.filters.selectedUsers === null) {
          this.detailsData = [];
          this.detailsPagination.totalRows = 0;
          this.overviewBarStats = {
            inTrouble: 0,
            atRisk: 0,
            atGoal: 0,
          };
          this.overviewBarUserCounts = {
            inTrouble: 0,
            atRisk: 0,
            atGoal: 0,
          };
          this.detailsLoading = false;
          return;
        }

        this._detailsFetchCount += 1;
        const params = this._buildFilterParams();
        params.append('page', page);
        if (sortKey) {
          params.append('sort_key', sortKey);
          this.detailsPagination.sortKey = sortKey;
        }
        if (sortOrder) {
          params.append('sort_order', sortOrder);
          this.detailsPagination.sortOrder = sortOrder;
        }
        if (this.statusFilter.length > 0) {
          params.append('status', this.statusFilter.join(','));
        }

        const data = await productivityApi.getDetails(params);

        this.detailsData = Array.isArray(data?.data) ? data.data : [];
        this.detailsPagination.totalRows = data?.total_count || 0;
        this.detailsPagination.page = page;

        if (data?.status_counts) {
          this.overviewBarStats = {
            inTrouble: data.status_counts.in_trouble || 0,
            atRisk: data.status_counts.at_risk || 0,
            atGoal: data.status_counts.at_goal || 0,
          };
        }
        if (data?.user_counts) {
          this.overviewBarUserCounts = {
            inTrouble: data.user_counts.in_trouble || 0,
            atRisk: data.user_counts.at_risk || 0,
            atGoal: data.user_counts.at_goal || 0,
          };
        }

        const isEmptyWithAllSelected =
          (data?.total_count ?? 0) === 0 &&
          this.filters.selectedUsers !== null &&
          this.filters.selectedUsers.length === 0 &&
          this._detailsFetchCount > 1;
        if (isEmptyWithAllSelected) {
          this.filters.selectedUsers = null;
          this.detailsData = [];
          this.detailsPagination.totalRows = 0;
          this.overviewBarStats = { inTrouble: 0, atRisk: 0, atGoal: 0 };
          this.overviewBarUserCounts = { inTrouble: 0, atRisk: 0, atGoal: 0 };
        }
      } catch {
        this.detailsData = [];
        this.overviewBarStats = { inTrouble: 0, atRisk: 0, atGoal: 0 };
        this.overviewBarUserCounts = { inTrouble: 0, atRisk: 0, atGoal: 0 };
      } finally {
        this.detailsLoading = false;
      }
    },

    async saveJournalEntry(productivityId, edits) {
      await productivityApi.saveJournalAdjustment(productivityId, edits);
      await this.fetchDetails(
        this.detailsPagination.page,
        this.detailsPagination.sortKey,
        this.detailsPagination.sortOrder
      );
      this.editingEntryId = null;
      this.journalEdits = [];
    },

    async applyFilters() {
      this.detailsPagination.page = 1;
      await this.fetchOverview();
    },

    setUserStatus(status) {
      this.filters.userStatus = status;
      this.applyFilters();
    },

    setDateRange(startDate, endDate) {
      this.filters.startDate = startDate;
      this.filters.endDate = endDate;
      this.applyFilters();
    },

    setSelectedUsers(users) {
      this.filters.selectedUsers = users;
      if (users === null) {
        this.detailsData = [];
        this.detailsPagination.totalRows = 0;
        this.overviewBarStats = { inTrouble: 0, atRisk: 0, atGoal: 0 };
        this.overviewBarUserCounts = { inTrouble: 0, atRisk: 0, atGoal: 0 };
      }
      this.applyFilters();
    },

    setStatusFilter(status) {
      if (status === 'all') {
        this.statusFilter = [];
      } else {
        this.statusFilter = [status];
      }
      this.fetchDetails(1);
    },

    toggleBarStatus(status) {
      // Get only the statuses that actually have data in the current bar
      const availableStatuses = [];
      if (this.overviewBarStats.inTrouble > 0)
        availableStatuses.push('in_trouble');
      if (this.overviewBarStats.atRisk > 0) availableStatuses.push('at_risk');
      if (this.overviewBarStats.atGoal > 0) availableStatuses.push('at_goal');

      // Check if all available statuses are currently selected
      const allAvailableSelected =
        availableStatuses.length > 0 &&
        availableStatuses.every(s => this.statusFilter.includes(s));

      // Check if showing all (no filter or all available selected)
      const hasAll = allAvailableSelected || this.statusFilter.length === 0;

      if (hasAll) {
        // If currently showing all, clicking a section should filter to just that section
        this.statusFilter = [status];
      } else if (this.statusFilter.includes(status)) {
        // If clicking an already-selected section, deselect it
        this.statusFilter = this.statusFilter.filter(s => s !== status);
      } else {
        // Add the clicked section to the filter
        const next = [...this.statusFilter, status];
        // If all available sections are now selected, reset to show all (empty filter)
        this.statusFilter =
          next.length === availableStatuses.length ? [] : next;
      }
      this.fetchDetails(1);
    },

    resetFilters() {
      const { startDate, endDate } = getDefaultDates();
      this.filters = {
        userStatus: 'all',
        startDate,
        endDate,
        selectedUsers: [],
      };
      this.statusFilter = [];
      this.applyFilters();
    },

    _buildFilterParams() {
      const params = new URLSearchParams();

      if (this.filters.startDate) {
        params.append('start_date', this.filters.startDate);
      }
      if (this.filters.endDate) {
        params.append('end_date', this.filters.endDate);
      }
      // Handle user selection: null = none, [] = all, array = specific users
      if (this.filters.selectedUsers === null) {
        params.append('user_ids', 'none');
      } else if (this.filters.selectedUsers?.length) {
        params.append(
          'user_ids',
          this.filters.selectedUsers.map(u => u.id).join(',')
        );
      }
      if (this.filters.userStatus === 'active') {
        params.append('active_only', 'true');
      } else if (this.filters.userStatus === 'inactive') {
        params.append('active_only', 'false');
      }

      return params;
    },
  },
});

function formatMonth(dateStr) {
  if (!dateStr) return '';

  let date;
  if (typeof dateStr === 'string') {
    if (
      dateStr.includes('T') ||
      (dateStr.includes(' ') && dateStr.includes(':'))
    ) {
      date = new Date(dateStr);
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = new Date(dateStr + 'T00:00:00');
    } else {
      date = new Date(dateStr);
    }
  } else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) {
    console.error('Invalid date format:', dateStr);
    return dateStr || '';
  }

  const months = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
}
